import React, { useMemo, useState, useCallback, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    IconButton,
    useTheme,
    Checkbox,
    Box,
    Paper,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
} from '@material-ui/core'
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import CloseIcon from '@material-ui/icons/Close'
import { SortableLocationForm } from '../../../SortableFinder/SortableLocationForm'
import { Container as FlexContainer } from '../../../../components/Container'
import { LocationSelection, Location } from '../../../../store/finder/types'
import {
    AreaConfig,
    AreaStatusTag,
    Folder,
    getUnitOccupancyCountMap,
    getUnitOccupancyType,
    ModelMap,
    TemplateService,
    Unit,
    UnitConfig,
    UnitOccupanyType,
} from '../../../../models'
import { UpdateTemplateServiceVendorParams } from '../../../../hooks/useEzSchedule/types'

const MODAL_WIDTH = 560
const MODAL_HEIGHT = 780

type OccupancyFilter = 'all' | 'full' | 'partial'

const getAllUnits = (folder: Folder): Unit[] => {
    let units = [...(folder.units || [])]
    ;(folder.children || []).forEach((child) => {
        units = units.concat(getAllUnits(child))
    })
    return units
}

export const pruneInfrastructure = (
    folder: Folder,
    keepUnit: (unit: Unit) => boolean,
): Folder | null => {
    const units = (folder.units || []).filter(keepUnit)
    const children = (folder.children || [])
        .map((child) => pruneInfrastructure(child, keepUnit))
        .filter((child): child is Folder => child != null)
    if (units.length === 0 && children.length === 0) return null
    return { ...folder, units, children }
}

const buildLocationSelectionFromUnitIds = (
    unitIds: number[],
    unitMap: Map<number, Unit>,
): LocationSelection => {
    const unit: LocationSelection['unit'] = Object.assign(
        {},
        ...unitIds
            .map((id) => {
                const u = unitMap.get(id)
                return u ? { [id]: u } : null
            })
            .filter(Boolean),
    )
    unit.length = Object.keys(unit).filter((k) => k !== 'length').length
    return {
        folder: Object.assign({}, { length: 0 }),
        unit,
    }
}

interface Props {
    open: boolean
    onClose: () => void
    infrastructure: Folder
    templateService: TemplateService
    updateTemplateServiceVendor: (
        params: UpdateTemplateServiceVendorParams,
    ) => void
    unitConfigMap: ModelMap<UnitConfig>
    areaConfigMap: ModelMap<AreaConfig>
    areaStatusTagMap: ModelMap<AreaStatusTag>
}

export const ServiceSpecifyUnitsModal = (props: Props) => {
    const {
        open,
        onClose,
        infrastructure,
        templateService,
        updateTemplateServiceVendor,
        unitConfigMap,
        areaConfigMap,
        areaStatusTagMap,
    } = props
    const theme = useTheme()
    const vendors = templateService.template_service_vendors

    const universeUnits = useMemo(() => getAllUnits(infrastructure), [
        infrastructure,
    ])

    const unitMap = useMemo(() => {
        const map = new Map<number, Unit>()
        universeUnits.forEach((u) => map.set(u.id, u))
        return map
    }, [universeUnits])

    const totalUnitCount = unitMap.size

    const [vendorAssignments, setVendorAssignments] = useState<
        Record<number, number[]>
    >(() => {
        const universeIds = new Set(
            getAllUnits(infrastructure).map((u) => u.id),
        )
        const initial: Record<number, number[]> = {}
        vendors.forEach((tsv) => {
            const unitIds =
                tsv.assigned_unit
                    ?.map((tu) => tu.unit?.id)
                    .filter(
                        (id): id is number =>
                            typeof id === 'number' && universeIds.has(id),
                    ) ?? []
            initial[tsv.id] = Array.from(new Set(unitIds))
        })
        return initial
    })

    const [currentVendorIndex, setCurrentVendorIndex] = useState(0)
    const [showNotice, setShowNotice] = useState(true)
    const [occupancyFilter, setOccupancyFilter] = useState<OccupancyFilter>(
        'all',
    )
    const [unitTypeIds, setUnitTypeIds] = useState<number[]>([])

    useEffect(() => {
        if (!open) return
        setOccupancyFilter('all')
        setUnitTypeIds([])
        setCurrentVendorIndex(0)
    }, [open, templateService.id])

    const currentVendor = vendors[currentVendorIndex]
    const currentVendorUnitIds = vendorAssignments[currentVendor?.id] ?? []

    const assignedUnitIds = useMemo(() => {
        const set = new Set<number>()
        Object.values(vendorAssignments).forEach((ids) =>
            ids.forEach((id) => set.add(id)),
        )
        return set
    }, [vendorAssignments])

    const unassignedCount = totalUnitCount - assignedUnitIds.size

    const hasChanges = useMemo(() => {
        const universeIds = new Set(universeUnits.map((unit) => unit.id))
        return vendors.some((tsv) => {
            const serverIds =
                tsv.assigned_unit
                    ?.map((tu) => tu.unit?.id)
                    .filter((id): id is number => typeof id === 'number') ?? []
            if (serverIds.some((id) => !universeIds.has(id))) return true
            const initIds = new Set(
                serverIds.filter((id) => universeIds.has(id)),
            )
            const currIds = new Set(vendorAssignments[tsv.id] ?? [])
            if (initIds.size !== currIds.size) return true
            return Array.from(initIds).some((id) => !currIds.has(id))
        })
    }, [vendors, vendorAssignments, universeUnits])

    const assignedToOtherVendors = useMemo(() => {
        const set = new Set<number>()
        Object.entries(vendorAssignments).forEach(([vid, ids]) => {
            if (Number(vid) !== currentVendor?.id) {
                ids.forEach((id) => set.add(id))
            }
        })
        return set
    }, [vendorAssignments, currentVendor?.id])

    const occupancyByUnitId = useMemo(() => {
        const map = new Map<number, UnitOccupanyType>()
        universeUnits.forEach((unit) => {
            map.set(
                unit.id,
                getUnitOccupancyType(
                    getUnitOccupancyCountMap(
                        unit,
                        areaConfigMap,
                        areaStatusTagMap,
                    ),
                ),
            )
        })
        return map
    }, [universeUnits, areaConfigMap, areaStatusTagMap])

    const unitTypeOptions = useMemo(() => {
        const counts = new Map<number, number>()
        universeUnits.forEach((unit) => {
            counts.set(
                unit.unit_config,
                (counts.get(unit.unit_config) ?? 0) + 1,
            )
        })
        return Array.from(counts.entries())
            .map(([id, count]) => ({
                id,
                name: unitConfigMap[id]?.name ?? `Unit type ${id}`,
                count,
            }))
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [universeUnits, unitConfigMap])

    const displayInfrastructure = useMemo(() => {
        const selectedTypes = new Set(unitTypeIds)
        const pruned = pruneInfrastructure(infrastructure, (unit) => {
            if (
                selectedTypes.size > 0 &&
                !selectedTypes.has(unit.unit_config)
            ) {
                return false
            }
            if (occupancyFilter === 'all') return true
            const occupancy = occupancyByUnitId.get(unit.id)
            if (occupancyFilter === 'full') {
                return occupancy === UnitOccupanyType.FULL_VACANT
            }
            return occupancy === UnitOccupanyType.PARTIAL
        })
        if (pruned) return pruned
        return { ...infrastructure, units: [], children: [] }
    }, [infrastructure, unitTypeIds, occupancyFilter, occupancyByUnitId])

    const shownAssignableCount = useMemo(() => {
        return getAllUnits(displayInfrastructure).filter(
            (unit) => !assignedToOtherVendors.has(unit.id),
        ).length
    }, [displayInfrastructure, assignedToOtherVendors])

    const locationSelection = useMemo(
        () => buildLocationSelectionFromUnitIds(currentVendorUnitIds, unitMap),
        [currentVendorUnitIds, unitMap],
    )

    const setLocationSelection = useCallback(
        (location: Location) => {
            if (!currentVendor) return
            const vendorId = currentVendor.id
            setVendorAssignments((prev) => {
                const currentIds = [...(prev[vendorId] ?? [])]
                if (location.type === 'unit') {
                    const unit = location.location as Unit
                    if (assignedToOtherVendors.has(unit.id)) return prev
                    const idx = currentIds.indexOf(unit.id)
                    const newIds =
                        idx >= 0
                            ? currentIds.filter((_, i) => i !== idx)
                            : [...currentIds, unit.id]
                    return { ...prev, [vendorId]: newIds }
                }
                if (location.type === 'folder') {
                    const folder = location.location as Folder
                    const availableToToggle = getAllUnits(folder).filter(
                        (u) => !assignedToOtherVendors.has(u.id),
                    )
                    const allCurrent = availableToToggle.every((u) =>
                        currentIds.includes(u.id),
                    )
                    const newIds = allCurrent
                        ? currentIds.filter(
                              (id) =>
                                  !availableToToggle.some(
                                      (unit) => unit.id === id,
                                  ),
                          )
                        : Array.from(
                              new Set(
                                  currentIds.concat(
                                      availableToToggle.map((unit) => unit.id),
                                  ),
                              ),
                          )
                    return { ...prev, [vendorId]: newIds }
                }
                return prev
            })
        },
        [currentVendor, assignedToOtherVendors],
    )

    const assignShown = useCallback(() => {
        if (!currentVendor || shownAssignableCount === 0) return
        const vendorId = currentVendor.id
        const shownIds = getAllUnits(displayInfrastructure)
            .filter((unit) => !assignedToOtherVendors.has(unit.id))
            .map((unit) => unit.id)
        setVendorAssignments((prev) => ({
            ...prev,
            [vendorId]: Array.from(
                new Set([...(prev[vendorId] ?? []), ...shownIds]),
            ),
        }))
    }, [
        currentVendor,
        shownAssignableCount,
        displayInfrastructure,
        assignedToOtherVendors,
    ])

    const getUnitAdornment = useCallback(
        (unit: Unit) => {
            const assignedToOther = assignedToOtherVendors.has(unit.id)
            const checked = currentVendorUnitIds.includes(unit.id)
            return (
                <FlexContainer style={{ flex: 1 }}>
                    <div style={{ flex: 1 }} />
                    <Checkbox
                        checked={checked}
                        disabled={assignedToOther}
                        onClick={() => {
                            if (!assignedToOther) {
                                setLocationSelection({
                                    type: 'unit',
                                    location: unit,
                                })
                            }
                        }}
                    />
                </FlexContainer>
            )
        },
        [assignedToOtherVendors, currentVendorUnitIds, setLocationSelection],
    )

    const getFolderAdornment = useCallback(
        (folder: Folder) => {
            const folderUnits = getAllUnits(folder)
            const availableUnits = folderUnits.filter(
                (u) => !assignedToOtherVendors.has(u.id),
            )
            const allSelected =
                availableUnits.length > 0 &&
                availableUnits.every((u) => currentVendorUnitIds.includes(u.id))
            const someSelected = availableUnits.some((u) =>
                currentVendorUnitIds.includes(u.id),
            )
            const disabled = availableUnits.length === 0
            return (
                <FlexContainer style={{ flex: 1 }}>
                    <div style={{ flex: 1 }} />
                    <Checkbox
                        color="primary"
                        checked={allSelected}
                        indeterminate={someSelected && !allSelected}
                        disabled={disabled}
                        onClick={(e) => {
                            e.stopPropagation()
                            if (!disabled) {
                                setLocationSelection({
                                    type: 'folder',
                                    location: folder,
                                })
                            }
                        }}
                    />
                </FlexContainer>
            )
        },
        [assignedToOtherVendors, currentVendorUnitIds, setLocationSelection],
    )

    const handleSave = useCallback(() => {
        vendors.forEach((tsv) => {
            const unitIds = vendorAssignments[tsv.id] ?? []
            updateTemplateServiceVendor({
                templateServiceVendorId: tsv.id,
                units: unitIds,
            })
        })
        onClose()
    }, [vendors, vendorAssignments, updateTemplateServiceVendor, onClose])

    if (!currentVendor) return null

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                style: {
                    width: MODAL_WIDTH,
                    height: MODAL_HEIGHT,
                    maxWidth: '100%',
                    maxHeight: 'calc(100vh - 32px)',
                    borderRadius: theme.shape.borderRadius * 2,
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                component="div"
                style={{
                    padding: theme.spacing(2, 2, 1.5, 2),
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    flexShrink: 0,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                }}
            >
                <Box
                    flex={1}
                    minWidth={0}
                    style={{ paddingRight: theme.spacing(1) }}
                >
                    <Typography variant="h6" display="block">
                        Assign Units
                    </Typography>
                    <Typography
                        variant="h6"
                        display="block"
                        style={{ marginTop: 4, fontWeight: 700 }}
                        noWrap
                    >
                        {templateService.service.name}
                    </Typography>
                </Box>
                <IconButton
                    size="small"
                    onClick={onClose}
                    aria-label="Close"
                    style={{ flexShrink: 0 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                style={{
                    padding: theme.spacing(0, 2, 2),
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                }}
            >
                {showNotice && (
                    <Box
                        style={{
                            backgroundColor: '#E8F5E9',
                            border: `1px solid ${theme.palette.success.light}`,
                            borderRadius: theme.shape.borderRadius,
                            padding: theme.spacing(1.5, 2),
                            marginBottom: theme.spacing(2),
                            position: 'relative',
                        }}
                    >
                        <IconButton
                            size="small"
                            onClick={() => setShowNotice(false)}
                            aria-label="Dismiss notice"
                            style={{
                                position: 'absolute',
                                top: 4,
                                right: 4,
                                padding: 2,
                            }}
                        >
                            <CloseIcon style={{ fontSize: 16 }} />
                        </IconButton>
                        <Typography
                            variant="subtitle2"
                            style={{
                                color: theme.palette.success.dark,
                                fontWeight: 700,
                                marginBottom: theme.spacing(0.5),
                            }}
                        >
                            Notice:
                        </Typography>
                        <ul
                            style={{
                                margin: 0,
                                paddingLeft: theme.spacing(2.5),
                                display: 'grid',
                                gap: theme.spacing(0.5),
                            }}
                        >
                            <li>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    Assigning units to vendors is optional.
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    If you do nothing, work will be spread out
                                    evenly between the vendors.
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    If you assign any units for this service,
                                    you must assign all of them.
                                </Typography>
                            </li>
                            <li>
                                <Typography
                                    variant="body2"
                                    style={{
                                        color: theme.palette.text.primary,
                                    }}
                                >
                                    You will not be able to save if any units
                                    are unassigned.
                                </Typography>
                            </li>
                        </ul>
                    </Box>
                )}

                <Paper
                    variant="outlined"
                    style={{
                        padding: theme.spacing(1.5, 2),
                        marginBottom: theme.spacing(2),
                        backgroundColor: theme.palette.grey[50],
                        borderRadius: theme.shape.borderRadius,
                    }}
                >
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="body2" color="textSecondary">
                            Unassigned Units
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            style={{
                                fontWeight: 600,
                                color:
                                    unassignedCount > 0
                                        ? theme.palette.text.primary
                                        : theme.palette.text.secondary,
                            }}
                        >
                            {unassignedCount === totalUnitCount
                                ? '-'
                                : unassignedCount}
                        </Typography>
                    </Box>
                </Paper>

                <Paper
                    variant="outlined"
                    style={{
                        padding: theme.spacing(1.5, 2),
                        marginBottom: theme.spacing(2),
                        display: 'flex',
                        alignItems: 'center',
                        borderRadius: theme.shape.borderRadius,
                    }}
                >
                    <IconButton
                        size="small"
                        disabled={currentVendorIndex <= 0}
                        onClick={() =>
                            setCurrentVendorIndex((i) => Math.max(0, i - 1))
                        }
                        aria-label="Previous vendor"
                    >
                        <ArrowBackIosIcon fontSize="small" />
                    </IconButton>
                    <Box flex={1} textAlign="center" px={1}>
                        <Typography variant="subtitle2" color="textSecondary">
                            Vendor {currentVendorIndex + 1} of {vendors.length}
                        </Typography>
                        <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600 }}
                        >
                            {currentVendor.vendor.name}
                        </Typography>
                    </Box>
                    <IconButton
                        size="small"
                        disabled={currentVendorIndex >= vendors.length - 1}
                        onClick={() =>
                            setCurrentVendorIndex((i) =>
                                Math.min(vendors.length - 1, i + 1),
                            )
                        }
                        aria-label="Next vendor"
                    >
                        <ArrowForwardIosIcon fontSize="small" />
                    </IconButton>
                </Paper>

                <Box style={{ marginBottom: theme.spacing(1) }}>
                    <Box display="flex" alignItems="center">
                        <FormControl
                            variant="outlined"
                            style={{
                                minWidth: 120,
                                marginRight: theme.spacing(1),
                            }}
                        >
                            <InputLabel id="assign-units-turn-label">
                                Turn
                            </InputLabel>
                            <Select
                                labelId="assign-units-turn-label"
                                label="Turn"
                                value={occupancyFilter}
                                onChange={(event) =>
                                    setOccupancyFilter(
                                        event.target.value as OccupancyFilter,
                                    )
                                }
                            >
                                <MenuItem value="all">All</MenuItem>
                                <MenuItem value="full">Full</MenuItem>
                                <MenuItem value="partial">Partial</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl
                            variant="outlined"
                            style={{ flex: 1, minWidth: 0 }}
                        >
                            <InputLabel id="assign-units-type-label" shrink>
                                Unit types
                            </InputLabel>
                            <Select
                                labelId="assign-units-type-label"
                                label="Unit types"
                                multiple
                                displayEmpty
                                value={unitTypeIds}
                                onChange={(event) => {
                                    const raw = event.target.value
                                    const ids = (Array.isArray(raw)
                                        ? raw
                                        : [raw]
                                    )
                                        .map(Number)
                                        .filter((id) => !Number.isNaN(id))
                                    setUnitTypeIds(ids)
                                }}
                                renderValue={(selected) => {
                                    const ids = selected as number[]
                                    if (ids.length === 0)
                                        return 'All unit types'
                                    return ids
                                        .map(
                                            (id) =>
                                                unitTypeOptions.find(
                                                    (option) =>
                                                        option.id === id,
                                                )?.name ?? `Unit type ${id}`,
                                        )
                                        .join(', ')
                                }}
                            >
                                {unitTypeOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox
                                            checked={unitTypeIds.includes(
                                                option.id,
                                            )}
                                            color="primary"
                                            style={{
                                                padding: 4,
                                                marginRight: 8,
                                                pointerEvents: 'none',
                                            }}
                                        />
                                        {`${option.name} (${option.count})`}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={assignShown}
                            disabled={shownAssignableCount === 0}
                        >
                            {`Assign shown (${shownAssignableCount})`}
                        </Button>
                    </Box>
                </Box>

                <Box
                    flex={1}
                    minHeight={0}
                    display="flex"
                    flexDirection="column"
                    style={{ overflow: 'hidden' }}
                >
                    <Paper
                        variant="outlined"
                        style={{
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column',
                            borderRadius: theme.shape.borderRadius,
                        }}
                    >
                        <SortableLocationForm
                            root={displayInfrastructure}
                            locationSelection={locationSelection}
                            setLocationSelection={setLocationSelection}
                            getUnitAdornment={getUnitAdornment}
                            getFolderAdornment={getFolderAdornment}
                            useCase="schedule"
                            maxHeight="100%"
                        />
                    </Paper>
                </Box>
            </DialogContent>

            <Divider />

            <DialogActions
                style={{
                    padding: theme.spacing(2),
                    gap: theme.spacing(1),
                }}
            >
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={
                        !hasChanges ||
                        (unassignedCount !== 0 &&
                            unassignedCount !== totalUnitCount)
                    }
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}
